import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { IconCheck, IconClose, IconError, IconInfo, IconWarning } from './icons';

/**
 * Ответ действия должен появляться там, где на него смотрят. Раньше все баннеры
 * рисовались вверху страницы, а кнопки стояли внизу длинной формы: нажимаешь
 * «Отклонить», ответ уезжает за пределы экрана, экран внешне не меняется — и
 * человек жмёт второй раз. Тосты держатся в правом нижнем углу, рядом с руками.
 *
 * Два свойства, которых не было и которые видно сразу: карточка уходит по
 * анимации, а не исчезает кадром (пропавшее без движения читается как сбой
 * отрисовки), и отсчёт останавливается, пока курсор над стопкой, — иначе текст
 * ошибки с кодом невозможно дочитать и скопировать.
 */
export type ToastTone = 'ok' | 'warn' | 'error' | 'info';

export interface ToastOptions {
  readonly tone?: ToastTone;
  /** Подробность второй строкой: код ошибки, идентификатор, «получателей 0». */
  readonly detail?: string;
  readonly durationMs?: number;
}

interface Toast extends ToastOptions {
  readonly id: number;
  readonly text: string;
  readonly tone: ToastTone;
  readonly leaving?: boolean;
}

export interface ToastApi {
  readonly show: (text: string, options?: ToastOptions) => void;
  readonly ok: (text: string, detail?: string) => void;
  readonly warn: (text: string, detail?: string) => void;
  readonly error: (text: string, detail?: string) => void;
}

const ToastContext = createContext<ToastApi | undefined>(undefined);

export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (!api) throw new Error('useToast вне ToastProvider');
  return api;
}

const TONE_STYLE: Readonly<Record<ToastTone, string>> = {
  ok: 'border-ok-line bg-ok-bg text-ok',
  warn: 'border-warn-line bg-warn-bg text-warn',
  error: 'border-danger-line bg-danger-bg text-danger',
  info: 'border-line bg-surface text-ink',
};

const TONE_ICON: Readonly<Record<ToastTone, (p: { size?: number }) => React.JSX.Element>> = {
  ok: IconCheck,
  warn: IconWarning,
  error: IconError,
  info: IconInfo,
};

const DEFAULT_MS: Readonly<Record<ToastTone, number>> = {
  ok: 4000,
  warn: 8000,
  error: 12_000,
  info: 5000,
};

const EXIT_MS = 140;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<readonly Toast[]>([]);
  const nextId = useRef(1);
  const timers = useRef(new Map<number, number>());
  const deadlines = useRef(new Map<number, { readonly at: number; readonly left: number }>());
  const [paused, setPaused] = useState(false);

  const remove = useCallback((id: number) => {
    setToasts((current) => current.map((t) => (t.id === id ? { ...t, leaving: true } : t)));
    window.setTimeout(
      () => setToasts((current) => current.filter((t) => t.id !== id)),
      EXIT_MS,
    );
    deadlines.current.delete(id);
  }, []);

  const arm = useCallback(
    (id: number, ms: number) => {
      if (ms <= 0) return;
      const existing = timers.current.get(id);
      if (existing) window.clearTimeout(existing);
      deadlines.current.set(id, { at: Date.now(), left: ms });
      timers.current.set(
        id,
        window.setTimeout(() => {
          timers.current.delete(id);
          remove(id);
        }, ms),
      );
    },
    [remove],
  );

  const dismiss = useCallback(
    (id: number) => {
      const timer = timers.current.get(id);
      if (timer) window.clearTimeout(timer);
      timers.current.delete(id);
      remove(id);
    },
    [remove],
  );

  const show = useCallback(
    (text: string, options: ToastOptions = {}) => {
      const tone = options.tone ?? 'info';
      const id = nextId.current++;
      const toast: Toast = { ...options, id, text, tone };
      setToasts((current) => [...current.slice(-3), toast]);
      arm(id, options.durationMs ?? DEFAULT_MS[tone]);
    },
    [arm],
  );

  // Пока курсор над стопкой, отсчёт стоит: остаток дожидается ухода курсора.
  useEffect(() => {
    if (paused) {
      for (const [id, timer] of timers.current) {
        window.clearTimeout(timer);
        const mark = deadlines.current.get(id);
        if (mark) {
          deadlines.current.set(id, {
            at: Date.now(),
            left: Math.max(600, mark.left - (Date.now() - mark.at)),
          });
        }
      }
      timers.current.clear();
      return;
    }
    for (const [id, mark] of deadlines.current) {
      if (!timers.current.has(id)) arm(id, mark.left);
    }
  }, [paused, arm]);

  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const timer of pending.values()) window.clearTimeout(timer);
      pending.clear();
    };
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      show,
      ok: (text, detail) => show(text, { tone: 'ok', ...(detail ? { detail } : {}) }),
      warn: (text, detail) => show(text, { tone: 'warn', ...(detail ? { detail } : {}) }),
      error: (text, detail) => show(text, { tone: 'error', ...(detail ? { detail } : {}) }),
    }),
    [show],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        className="pointer-events-none fixed right-3 bottom-3 z-50 flex w-[min(26rem,calc(100vw-1.5rem))] flex-col gap-2 sm:right-4 sm:bottom-4"
        role="status"
        aria-live="polite"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        {toasts.map((toast) => {
          const Glyph = TONE_ICON[toast.tone];
          return (
            <div
              key={toast.id}
              className={`pointer-events-auto flex items-start gap-2.5 rounded-card border px-3.5 py-2.5 shadow-pop ${TONE_STYLE[toast.tone]}`}
              style={{
                animation: toast.leaving
                  ? `moika-toast-out ${EXIT_MS}ms ease-in forwards`
                  : 'moika-toast-in 140ms ease-out',
              }}
            >
              <Glyph size={16} />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium break-words">{toast.text}</p>
                {toast.detail ? (
                  <p className="mt-0.5 font-mono text-xs break-words opacity-80">{toast.detail}</p>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => dismiss(toast.id)}
                className="touch-exempt -mt-0.5 shrink-0 rounded p-0.5 opacity-50 transition hover:opacity-100"
                aria-label="Закрыть"
              >
                <IconClose size={14} />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}
